import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../../basics/interface/CustomModal"
import { COLORS } from "../../../utilities/Constants"

// Transfert (batch) d'une commande de stock virtuel vers le stock physique.
// Une ligne par article restant : quantité à transférer (0 = ignorer) +
// article physique cible, pré-rempli via les liens appris numéro → article.
// Le backend crée UNE commande physique avec un lot par ligne transférée.
const TransfertModalV2 = ({
	commande,
	categories,
	allArticles,
	liens,
	postState,
	// Quantités initiales par id d'article virtuel (raccourci depuis la clé
	// d'un article : lui = son dispo, les autres = 0). Sinon défaut = dispo.
	initialQuantites = null,
	onTransferer,
	onClose,
}) => {
	// Dispo = reçu - utilisé - réservé (les réservations des fiches ouvertes
	// restent intouchables, contrairement à Consommer qui les ignore).
	const dispo = (a) =>
		a.quantite_recue - a.quantite_utilisee - (a.quantite_reservee || 0)

	// Lignes « Depot » exclues : placeholder financier, pas des pièces.
	const isDepot = (a) => a.numero === "" && a.label === "Depot"

	const [lignes, setLignes] = useState(() =>
		(commande.articles || [])
			.filter((a) => dispo(a) > 0 && !isDepot(a))
			.map((a) => {
				const lien = liens.find(
					(l) => a.numero !== "" && l.numero === a.numero,
				)
				const article = lien
					? allArticles.find((art) => art.id === lien.article)
					: null
				return {
					av: a,
					max: dispo(a),
					quantite:
						initialQuantites === null
							? String(dispo(a))
							: String(initialQuantites[a.id] || 0),
					categorieId: article ? article.categorie : 0,
					articleId: article ? article.id : 0,
				}
			}),
	)
	const [confirm, setConfirm] = useState(false)

	const busy = postState.saving || postState.saved

	const update = (index, patch) => {
		setConfirm(false)
		setLignes((prev) =>
			prev.map((l, i) => (i === index ? { ...l, ...patch } : l)),
		)
	}

	const setQuantite = (index, value, max) => {
		if (value !== "" && parseFloat(value) > max)
			update(index, { quantite: String(max) })
		else update(index, { quantite: value })
	}

	const actives = lignes.filter((l) => parseFloat(l.quantite) > 0)
	const invalide =
		actives.length === 0 || actives.some((l) => l.articleId === 0)

	const transferer = () => {
		if (!confirm) {
			setConfirm(true)
			return
		}
		onTransferer(
			commande.id,
			actives.map((l) => ({
				idArticleVirtuel: l.av.id,
				idArticle: l.articleId,
				quantite: parseFloat(l.quantite),
			})),
		)
	}

	return (
		<CustomModal
			title="Transférer dans le stock"
			subtitle={`${commande.fournisseur_nom} — ${commande.numero_commande}`}
			onClose={onClose}
			maxWidth={860}
			showCloseButton={!busy}
			showFooter={!busy}
			buttons={[
				{ title: "Annuler", styleName: "secondary", onClick: onClose },
				{
					title: confirm ? "Confirmer ?" : "Transférer",
					styleName: "primary",
					onClick: transferer,
					disabled: invalide,
				},
			]}
		>
			{busy ? (
				<div style={s.feedback}>
					{postState.saving ? (
						<FontAwesomeIcon icon={faSync} spin size="2x" />
					) : (
						<FontAwesomeIcon
							icon={faCheck}
							size="2x"
							style={{ color: COLORS.success }}
						/>
					)}
				</div>
			) : lignes.length === 0 ? (
				<div style={s.hint}>Aucune quantité disponible à transférer.</div>
			) : (
				<>
					<div style={s.hint}>
						Quantité à 0 = ligne ignorée. Chaque ligne transférée crée
						un lot dans le stock au prix d'achat de l'article virtuel.
					</div>
					<div style={{ ...s.row, ...s.headRow }}>
						<span style={s.colLabel}>Article virtuel</span>
						<span style={s.colNumero}>Numéro</span>
						<span style={s.colDispo}>Dispo</span>
						<span style={s.colQte}>Quantité</span>
						<span style={s.colSelect}>Catégorie</span>
						<span style={s.colSelect}>Article stock</span>
					</div>
					{lignes.map((l, i) => {
						const ignoree = !(parseFloat(l.quantite) > 0)
						return (
							<div
								key={l.av.id}
								style={{
									...s.row,
									opacity: ignoree ? 0.45 : 1,
								}}
							>
								<span
									style={{ ...s.colLabel, fontWeight: 600 }}
									title={l.av.label}
								>
									{l.av.label}
								</span>
								<span style={s.colNumero} title={l.av.numero}>
									{l.av.numero}
								</span>
								<span style={s.colDispo}>{l.max}</span>
								<input
									type="number"
									style={{ ...s.input, ...s.colQte }}
									value={l.quantite}
									onFocus={(e) => e.target.select()}
									onChange={(e) =>
										setQuantite(i, e.target.value, l.max)
									}
								/>
								<select
									style={{ ...s.input, ...s.colSelect }}
									value={l.categorieId}
									onChange={(e) =>
										update(i, {
											categorieId: parseInt(e.target.value),
											articleId: 0,
										})
									}
								>
									<option value={0}>-</option>
									{categories.map((c) => (
										<option key={c.id} value={c.id}>
											{c.nom}
										</option>
									))}
								</select>
								<select
									style={{ ...s.input, ...s.colSelect }}
									value={l.articleId}
									onChange={(e) =>
										update(i, {
											articleId: parseInt(e.target.value),
										})
									}
								>
									<option value={0}>-</option>
									{allArticles
										.filter(
											(a) =>
												l.categorieId === 0 ||
												a.categorie === l.categorieId,
										)
										.map((a) => (
											<option key={a.id} value={a.id}>
												{a.nom}
											</option>
										))}
								</select>
							</div>
						)
					})}
				</>
			)}
		</CustomModal>
	)
}

const s = {
	feedback: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: "24px 0",
		color: COLORS.textMuted,
	},
	hint: {
		fontSize: 13,
		color: COLORS.textMuted,
		padding: "2px 0 12px",
	},
	row: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		marginBottom: 10,
	},
	headRow: {
		marginBottom: 6,
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
	},
	colLabel: {
		flex: "1 1 140px",
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		fontSize: 13,
	},
	colNumero: {
		flex: "0 0 90px",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		fontSize: 13,
		color: COLORS.textMuted,
	},
	colDispo: {
		flex: "0 0 44px",
		textAlign: "right",
		fontSize: 13,
		fontVariantNumeric: "tabular-nums",
	},
	colQte: { flex: "0 0 76px" },
	colSelect: { flex: "1 1 130px", minWidth: 0 },
	input: {
		height: 36,
		padding: "0 10px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 13,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
}

export default TransfertModalV2
